import { lazy, Suspense, useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import type { Slot } from "@/src/types";
import { useGetTenantQuery, useGetProceduresQuery } from "@/src/api/Tenant";
import {
  usePublicConsultationQuery,
  useAvailableSlotsQuery,
  useBookAppointmentMutation,
  useNotifyMutation,
} from "@/src/api/Consultation";
import OlMap, { ParcelInfo } from "@/src/components/Map/OlMap";
import LayerControl, {
  DEFAULT_LAYER_VISIBILITY,
  DEFAULT_LAYER_OPACITY,
  LayerVisibilityState,
  LayerOpacityState,
} from "@/src/components/Map/LayerControl";
import type { CustomWmsLayer } from "@/src/components/Map/AddWmsDialog";
import { stableCustomLayerId } from "@/src/lib/wmsCatalog";
import { getProcedureMapView } from "@/src/lib/procedureMapView";
import { isProcedureActive, pickDefaultProcedure } from "@/src/lib/procedureStatus";

// Lazy — dialog WMS (582 linie + fetch GetCapabilities) potrzebny dopiero
// po kliknięciu "Dodaj warstwę WMS"; zamknięty i tak zwraca null.
const AddWmsDialog = lazy(() => import("@/src/components/Map/AddWmsDialog"));
// Nachylenie terenu — tylko mapa konsultanta, osobny chunk.
const SlopePanel = lazy(() => import("@/src/components/Map/SlopePanel"));
import { getAvailableBuiltinLayerIds, migrateCustomOrder } from "@/src/lib/layerAvailability";
import { getMpzpFileUrl } from "@/src/api/Tenant";
import { useCreateApplicationDraftMutation } from "@/src/api/Application";
import { useLogConsultantSearch, type ConsultantSearchInput } from "@/src/api/ConsultantSearchLog";
import { PlanFeatureProperties, useGetPlanFeaturesQuery, useGetPlanMetaQuery, useGetRasterMetaQuery } from "@/src/api/Plan";
import PlanFeatureDetails from "@/src/pages/PlanAdmin/PlanFeatureDetails";
import PlanSearchBox from "@/src/pages/PlanAdmin/PlanSearchBox";
import FloatingPanel from "@/src/components/Map/FloatingPanel";
import MeasureTools from "@/src/components/Map/MeasureTools";
import PlanLegend from "@/src/pages/PlanAdmin/PlanLegend";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import useUldk from "@/src/hooks/useUldk";
import { Trash2, Search, MapPin, Calendar, ChevronRight, ClipboardList, FlaskConical, Map as MapIcon, X, StickyNote } from "lucide-react";
import { useSurveyAdminContext } from "@/src/api/Survey";
import { getThemeStyles } from "@/src/lib/theme";
import ProcedureDownloads from "@/src/components/ProcedureDownloads/ProcedureDownloads";
import ZoomPanImage from "@/src/components/ZoomPanImage/ZoomPanImage";
import ParcelZonesInfo, { useMpzpZonesStatus } from "@/src/components/ParcelZones/ParcelZonesInfo";
import { sanitizeHtml } from "@/src/lib/sanitizeHtml";

type SelectedParcel = {
  id: string;
  geometry: any;
  info?: ParcelInfo | null;
};

/**
 * TenantPublicPage — publiczna strona procedury (mapa składania wniosków/uwag).
 *
 * Re-używana również w panelu konsultanta (ConsultantMapPage) z propem
 * `consultantMode={true}`. W tym trybie:
 *  - URL nie ma slug-u procedury — ID przekazane przez `procedureIdOverride`
 *  - Brak nagłówka z linkiem "Wróć do listy"
 *  - Brak przycisku "Złóż uwagę dla wybranych działek"
 *  - Sidebar pomija formularz wnioskowania (sekcje z linkiem do formularza)
 *  - Klik w działkę woła `onConsultantParcelClick(parcelId)` zamiast nawigacji
 *    do CreateApplicationPage — nadrzędny komponent otwiera modal notatki.
 *  - Popup wyboru akcji ma label „Dodaj notatkę projektanta" zamiast
 *    „Wskaż działkę do wniosku/uwagi".
 */
export type TenantPublicPageProps = {
  consultantMode?: boolean;
  procedureIdOverride?: string;
  onConsultantParcelClick?: (parcelId: string) => void;
};

function TenantPublicPage({ consultantMode, procedureIdOverride, onConsultantParcelClick }: TenantPublicPageProps = {}) {
  const params = useParams({ strict: false }) as { tenant: string; procedureSlug?: string };
  const tenant = params.tenant;
  // W consultantMode ignorujemy slug z URL — procedurę dyktuje rodzic propem.
  const procedureSlug = consultantMode ? undefined : params.procedureSlug;
  // Aktualnie wybrana procedura do wyświetlenia w prawym panelu landing page'a.
  // Domyślnie ustawiana na pierwszą aktywną (w effect po załadowaniu procedures).
  const [previewProcedureId, setPreviewProcedureId] = useState<string | null>(null);
  // Czy blok "Ogłoszenie" jest rozwinięty (pełna wysokość) czy skrócony do
  // wysokości lewej kolumny. Domyślnie skrócony — użytkownik może rozwinąć.
  const [announcementExpanded, setAnnouncementExpanded] = useState(false);
  // Czy user już wcześniej odwiedzał stronę gminy — po revisit zwijamy
  // sekcje konsultacji / ogłoszenia domyślnie (mniej szumu). Flagę
  // ustawiamy w localStorage po pierwszym załadowaniu.
  const isRevisit = useMemo(() => {
    try { return localStorage.getItem(`${tenant}.visited`) === "1"; } catch { return false; }
  }, [tenant]);
  useEffect(() => {
    try { localStorage.setItem(`${tenant}.visited`, "1"); } catch { /* ignore */ }
  }, [tenant]);
  // Sekcje zwijane po revisit. Na pierwszej wizycie — rozwinięte (user widzi
  // pełen kontekst). Po revisit — zwinięte (szybciej do akcji).
  const [consultationsCollapsed, setConsultationsCollapsed] = useState(isRevisit);
  // Konsultacje na single-procedure: zwinięte do 1 akapitu.
  const [consultationInfoCollapsed, setConsultationInfoCollapsed] = useState(true);
  // Refy do pomiaru wysokości lewej kolumny — ogłoszenie ma maksymalnie
  // taką samą wysokość, reszta tekstu pod "Rozwiń".
  const leftColumnRef = useRef<HTMLDivElement | null>(null);
  const announcementContentRef = useRef<HTMLDivElement | null>(null);
  const [leftColumnHeight, setLeftColumnHeight] = useState<number | null>(null);
  const [announcementOverflows, setAnnouncementOverflows] = useState(false);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [bookingForm, setBookingForm] = useState({ name: "", email: "", phone: "" });
  const [selectedSlot, setSelectedSlot] = useState<{ time: string; consultantNumber: number } | null>(null);
  const [bookingStatus, setBookingStatus] = useState<string | null>(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyStatus, setNotifyStatus] = useState<string | null>(null);
  const [latLng, setLatLng] = useState({ lat: 49.62, lng: 20.69 });

  // Map parcel selection
  const [selectedParcels, setSelectedParcels] = useState<SelectedParcel[]>([]);
  // Plan Ogólny — features kliknięte na mapie (tablica posortowana wg
  // priorytetu: strefa → obszary specjalne). Popup pokazuje pierwszy
  // jako główny + listę pozostałych pod nim.
  const [selectedPlanFeatures, setSelectedPlanFeatures] = useState<PlanFeatureProperties[]>([]);
  // Instance ol/Map — przechwycona przez onMapReady, wystawiana do MeasureTools
  // osadzonego w FloatingPanelu "Wyszukiwarka stref i legenda" (pomiary
  // zamiast w prawym górnym rogu mapy są teraz w tym panelu).
  const [olMapInstance, setOlMapInstance] = useState<any>(null);
  // Mapa konsultanta: działka wskazana akcją „Nachylenie terenu działki".
  // `nonce` rozwija zminimalizowany panel także przy ponownym wyborze tej samej działki.
  const [slopeRequest, setSlopeRequest] = useState<{ parcelId: string; nonce: number } | null>(null);
  // Feature na który mapa ma zoomować (z PlanSearchBox).
  const [planFocusFeatureId, setPlanFocusFeatureId] = useState<number | null>(null);
  // Czy panel legendy stref jest widoczny (zwijalny).
  const [showLegend, setShowLegend] = useState(false);
  // Modal podglądu legendy planu miejscowego (PDF/JPG/PNG/TIF).
  const [legendaModalOpen, setLegendaModalOpen] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const panelRegistry = useRef(new Map<string, { x: number; y: number; width: number; height: number; minimized: boolean }>());
  // Klucze localStorage są per-procedura — różne procedury w tej samej gminie
  // mogą mieć inne warstwy defaultowe (ustawione przez admina w payload).
  // Dla stron gminy bez slug-a (landing) używamy suffixu `_none`.
  const procedureKey = procedureSlug || "_none";
  // Widoczność warstw mapy — localStorage per-procedura, fallback na payload.defaultLayerVisibility.
  const [layerVisibility, setLayerVisibility] = useState<LayerVisibilityState>(() => {
    try {
      const raw = localStorage.getItem(`mapLayers.${tenant}.${procedureKey}`);
      if (raw) return { ...DEFAULT_LAYER_VISIBILITY, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_LAYER_VISIBILITY;
  });
  useEffect(() => {
    try {
      localStorage.setItem(`mapLayers.${tenant}.${procedureKey}`, JSON.stringify(layerVisibility));
    } catch { /* ignore */ }
  }, [layerVisibility, tenant, procedureKey]);
  // Przezroczystość warstw — osobny localStorage key, też per-procedura.
  const [layerOpacity, setLayerOpacity] = useState<LayerOpacityState>(() => {
    try {
      const raw = localStorage.getItem(`mapOpacity.${tenant}.${procedureKey}`);
      if (raw) return { ...DEFAULT_LAYER_OPACITY, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_LAYER_OPACITY;
  });
  useEffect(() => {
    try {
      localStorage.setItem(`mapOpacity.${tenant}.${procedureKey}`, JSON.stringify(layerOpacity));
    } catch { /* ignore */ }
  }, [layerOpacity, tenant, procedureKey]);
  // Kolejność warstw na mapie (drag-drop z LayerControl). Items: LayerId dla
  // built-in, `custom:${id}` dla custom WMS. Undefined = domyślna kolejność
  // z LAYERS const (lub z payload.defaultLayerOrder admina).
  const [layerOrder, setLayerOrder] = useState<string[] | undefined>(() => {
    try {
      const raw = localStorage.getItem(`mapOrder.${tenant}.${procedureKey}`);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return undefined;
  });
  useEffect(() => {
    try {
      if (layerOrder) localStorage.setItem(`mapOrder.${tenant}.${procedureKey}`, JSON.stringify(layerOrder));
    } catch { /* ignore */ }
  }, [layerOrder, tenant, procedureKey]);
  // Niestandardowe warstwy WMS/WMTS — per-procedura w localStorage, z fallback
  // na `payload.defaultCustomWmsLayers` ustawionym przez admina.
  const [customWmsLayers, setCustomWmsLayers] = useState<CustomWmsLayer[]>(() => {
    try {
      const raw = localStorage.getItem(`mapCustomWms.${tenant}.${procedureKey}`);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  });
  useEffect(() => {
    try {
      localStorage.setItem(`mapCustomWms.${tenant}.${procedureKey}`, JSON.stringify(customWmsLayers));
    } catch { /* ignore */ }
  }, [customWmsLayers, tenant, procedureKey]);
  // Responsywność — na wąskich ekranach (< 768px) przełączamy layout
  // z ResizablePanel (side-by-side) na pionowy stack (mapa na górze,
  // panel pod spodem). matchMedia jest szybsze niż window.innerWidth
  // i reaguje na zmiany orientacji urządzenia.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  // Na mobile: panel (search/parcels/plan) jest zwijany pod mapą.
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [wmsDialogOpen, setWmsDialogOpen] = useState(false);
  const [parcelSearchObreb, setParcelSearchObreb] = useState("");
  // Input tekstowy dla autocomplete obrębu — trzymany osobno od TERYT value
  // (parcelSearchObreb) żeby user mógł szukać po nazwie/numerze bez od razu
  // wybierać wpisu. Po kliknięciu sugestii ustawiamy oba.
  const [parcelSearchObrebInput, setParcelSearchObrebInput] = useState("");
  const [parcelSearchObrebOpen, setParcelSearchObrebOpen] = useState(false);
  const [parcelSearchNumer, setParcelSearchNumer] = useState("");
  const [parcelSearchLoading, setParcelSearchLoading] = useState(false);
  // Fokus po wyczyszczeniu obrębu (X) i po wyszukaniu działki — obręb
  // zostaje, więc kolejny numer można wpisać od razu.
  const parcelSearchObrebRef = useRef<HTMLInputElement>(null);
  const parcelSearchNumerRef = useRef<HTMLInputElement>(null);
  const [addressQuery, setAddressQuery] = useState("");
  const [addressResults, setAddressResults] = useState<Array<{ display_name: string; lat: string; lon: string; place_id: number }>>([]);
  // Per-procedura: czy w karcie procedury jest rozwinięta sekcja "Umów
  // wizytę na dyżur projektanta" (lista sesji konsultacji). Tylko jedna
  // procedura naraz może mieć rozwinięte sesje (toggle by ID).
  const [expandedSessionsProcedure, setExpandedSessionsProcedure] = useState<string | null>(null);

  // Get tenant TERYT for parcel search - need to read after tenantQuery loads
  const tenantQuery = useGetTenantQuery(tenant);
  const consultationQuery = usePublicConsultationQuery(tenant);
  const proceduresQuery = useGetProceduresQuery(tenant);
  const slotsQuery = useAvailableSlotsQuery(tenant, selectedSession);
  const bookMutation = useBookAppointmentMutation(tenant, selectedSession);
  const notifyMutation = useNotifyMutation(tenant, selectedSession);

  const tenantPayload = tenantQuery.data?.payload;
  const tenantTerytConfig = tenantPayload?.tenant || "";
  const theme = getThemeStyles(tenantPayload?.primaryColor);

  const { getParcelGeometryById, getRegionGeometryById, precincts } = useUldk({ tenant: tenantTerytConfig });

  // Rejestr wyszukiwań — tylko na mapie konsultanta (mieszkańców nie logujemy).
  const logConsultantSearch = useLogConsultantSearch(tenant);
  const logSearch = (entry: Omit<ConsultantSearchInput, "procedureId">) => {
    if (consultantMode) logConsultantSearch({ ...entry, procedureId: procedureIdOverride });
  };

  const sessionsRaw = consultationQuery.data?.sessions || [];
  const pageSettings = consultationQuery.data?.pageSettings || null;
  // Sortuj konsultacje od najbliższej daty do najdalszej. Przy tej samej dacie
  // — po godzinie rozpoczęcia. Backend zwraca sesje w kolejności wpisu, co
  // przy kilku zaplanowanych konsultacjach wygląda przypadkowo dla obywatela.
  // Nieprzyszłe konsultacje (te z datą < dziś) trzymamy na końcu listy
  // posortowane chronologicznie (najnowsze przeszłe wyżej) — żeby archiwum
  // nie zaśmiecało góry, ale nie znikało.
  //
  // UWAGA: `sessions` MUSI być zadeklarowane PRZED `sessionsByKind` poniżej —
  // sessionsByKind jest useMemo z dep [sessions]; przy pierwszym renderze
  // React wywołuje factory, co w produkcyjnym bundlu Vite po minifikacji
  // odpala TDZ jeśli sessions jest zadeklarowane później.
  const sessions = useMemo(() => {
    const now = Date.now();
    const future: typeof sessionsRaw = [];
    const past: typeof sessionsRaw = [];
    for (const s of sessionsRaw) {
      if (new Date(s.consultationDate).getTime() >= now) future.push(s);
      else past.push(s);
    }
    const byDateAsc = (a: typeof sessionsRaw[0], b: typeof sessionsRaw[0]) => {
      const d = new Date(a.consultationDate).getTime() - new Date(b.consultationDate).getTime();
      if (d !== 0) return d;
      return (a.startTime || "").localeCompare(b.startTime || "");
    };
    future.sort(byDateAsc);
    past.sort((a, b) => byDateAsc(b, a)); // przeszłe odwrotnie — najnowsze przeszłe najpierw
    return [...future, ...past];
  }, [sessionsRaw]);

  // Grupuj sesje po polu `kind` — każdy rodzaj formy konsultacji
  // (Spotkanie otwarte / Dyżur projektanta / Warsztaty / itd.) wyświetlamy
  // jako osobną sekcję na publicznej stronie. W ramach grupy zachowujemy
  // posortowanie z `sessions` (przyszłe wg daty rosnąco, potem przeszłe).
  // Sesje bez ustawionego `kind` (NULL/empty) trafiają do grupy
  // "Inne formy konsultacji" — admin może je sklasyfikować w panelu.
  //
  // `Map` zachowuje insertion order — pierwsza napotkana sesja danego
  // `kind` (czyli najbliższa data) wyznacza kolejność grup. Najpilniejsza
  // forma (np. najbliższe spotkanie otwarte) idzie u góry listy.
  const sessionsByKind = useMemo(() => {
    const groups = new Map<string, typeof sessions>();
    for (const s of sessions) {
      const key = (s.kind || "").trim() || "Inne formy konsultacji";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(s);
    }
    return Array.from(groups.entries());
  }, [sessions]);

  // consultationInfo z bazy może być w starym formacie (plain text z \n)
  // albo nowym (HTML z RichTextEditor). Dla kompatybilności wstecznej,
  // jeśli treść nie zawiera tagów HTML, zamieniamy \n na <br/> żeby
  // historyczne wpisy nie zlewały się w jeden akapit po przeniesieniu
  // na dangerouslySetInnerHTML.
  const consultationInfoHtml = useMemo(() => {
    const raw = pageSettings?.consultationInfo || "";
    if (!raw.trim()) return "";
    const looksLikeHtml = /<\/?[a-z][\s\S]*?>/i.test(raw);
    return sanitizeHtml(looksLikeHtml ? raw : raw.replace(/\n/g, "<br/>"));
  }, [pageSettings?.consultationInfo]);
  const procedures = proceduresQuery.data || [];
  const tenantData = tenantQuery.data || null;

  const slots: Slot[] = slotsQuery.data?.slots || [];
  const hasAvailableSlots = slotsQuery.data?.hasAvailableSlots !== false;
  const numConsultants = slotsQuery.data?.session?.numConsultants || 1;
  const loadingSlots = slotsQuery.isFetching;
  const uniqueTimes = [...new Set(slots.map((s) => s.time))];

  // Tryb "single procedure" — gdy URL zawiera /$tenant/$procedureSlug, pokazujemy
  // tylko tą jedną procedurę (bez listy innych aktywnych procedur i bez sekcji
  // konsultacji społecznych). Jeżeli slug nie pasuje do żadnej procedury ->
  // zachowujemy landing page gminy (z listą wszystkich dostępnych procedur).
  // W consultantMode procedurę wybiera rodzic przez `procedureIdOverride`
  // (lookup po ID z listy procedur). W trybie publicznym używamy slug-a z URL.
  const slugProcedure = consultantMode && procedureIdOverride
    ? procedures.find((p) => p.id === procedureIdOverride)
    : procedureSlug
      ? procedures.find((p) => p.name === procedureSlug)
      : undefined;
  const isSingleProcedureMode = !!slugProcedure && (!!procedureSlug || !!consultantMode);
  const displayedProcedures = isSingleProcedureMode ? [slugProcedure!] : procedures;
  // Zalogowany admin tej gminy (świeży token) — dostaje panel testowy przy
  // procedurach, które jeszcze nie wystartowały (przyciski publiczne są
  // wtedy ukryte, a admin musi sprawdzić formularz uwagi i geoankietę
  // przed dniem startu konsultacji).
  const { isAdminHere } = useSurveyAdminContext(tenant);
  const slugMissingMatch = !!procedureSlug && !slugProcedure && procedures.length > 0;

  // Po załadowaniu procedury: ZAWSZE stosujemy admin defaults z payload —
  // admin konfiguracja jest sourcem prawdy. User tymczasowe zmiany w tej
  // sesji (toggle warstwy, drag-drop) pozostają w React state, ale po
  // reload widok wraca do admin settings. Dzięki temu panel "Warstwy mapy"
  // na publicznej stronie zawsze odpowiada temu co admin zapisał w oknie
  // edycji procedury (widoczność, kolejność, lista custom WMS).
  useEffect(() => {
    if (!slugProcedure) return;
    const payload: any = slugProcedure.payload ?? {};
    if (payload.defaultLayerVisibility && typeof payload.defaultLayerVisibility === "object") {
      setLayerVisibility({ ...DEFAULT_LAYER_VISIBILITY, ...payload.defaultLayerVisibility });
    }
    // Migruj stare efemeryczne custom ID na stabilne (bez tego user widzi
    // custom warstwy zawsze na końcu listy zamiast na pozycji jaką ustawił
    // admin). Migracja pozycyjna: i-ty custom w order ↔ i-ty w payload.
    const migratedOrder = migrateCustomOrder(
      payload.defaultLayerOrder,
      payload.defaultCustomWmsLayers,
    );
    if (Array.isArray(migratedOrder)) {
      setLayerOrder(migratedOrder);
    }
    if (Array.isArray(payload.defaultCustomWmsLayers)) {
      // Hydratacja admin defaults — ID stabilne (catalogId lub hash z URL+layerName),
      // identyczne z tym co EditProcedurePage generuje przy dodawaniu.
      // Dzięki temu `defaultLayerOrder` z payload (zawiera `custom:cat:...`
      // albo `custom:url:...`) pasuje do ID warstw po hydratacji, więc
      // `resolveRenderOrder` renderuje warstwy w admin-owej kolejności —
      // zamiast wrzucać je na koniec jako "missing".
      setCustomWmsLayers(
        payload.defaultCustomWmsLayers.map((l: any) => ({
          id: stableCustomLayerId({ catalogId: l.catalogId, url: l.url, layerName: l.layerName }),
          url: l.url,
          layerName: l.layerName,
          title: l.title,
          opacity: typeof l.opacity === "number" ? l.opacity : 0.8,
          visible: l.visible !== false,
          availableCrs: l.availableCrs,
          type: l.type,
          catalogId: l.catalogId,
        })),
      );
    } else {
      // Admin nie ustawił custom — wyczyść listę (żeby stare localStorage
      // z innej procedury nie przeciekało).
      setCustomWmsLayers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugProcedure?.id]);

  // Aktywna procedura i jej typ — wyliczone raz, używane do filtrowania warstw
  // i wyświetlania opisu konsultacji per procedura.
  const activeProcedure = isSingleProcedureMode
    ? slugProcedure
    : (procedures.find((p) => p.id === previewProcedureId) ?? null);
  const {
    center: procedureMapCenter,
    zoom: initialZoom,
  } = getProcedureMapView(activeProcedure?.payload);
  const procedureMapLat = procedureMapCenter?.lat;
  const procedureMapLng = procedureMapCenter?.lng;
  // Tereny MPZP (wektor) — gdy wgrane, pod wybraną działką pokazujemy
  // udział przeznaczeń terenu z odnośnikami do tekstu planu.
  const mpzpZonesStatus = useMpzpZonesStatus(
    tenant,
    isSingleProcedureMode ? activeProcedure?.id : undefined,
  );

  // Każdy wariant mapy publicznej (landing, konkretna procedura i tryb
  // konsultanta) otwiera się w punkcie zapisanym dla aktywnej procedury.
  useEffect(() => {
    if (procedureMapLat !== undefined && procedureMapLng !== undefined) {
      setLatLng({ lat: procedureMapLat, lng: procedureMapLng });
    }
  }, [
    activeProcedure?.id,
    procedureMapLat,
    procedureMapLng,
  ]);

  const activeActType = (activeProcedure?.payload as any)?.spatialPlanningActType ?? "";
  const isActivePlanOgolny = activeActType.includes("3.1") || /plan og[oó]lny/i.test(activeActType);
  // Raster meta — żeby wiedzieć nazwę warstwy i czy COG istnieje.
  const activeRasterMetaQ = useGetRasterMetaQuery(
    tenant,
    activeProcedure?.id ?? "",
    { enabled: !!activeProcedure?.id && !isActivePlanOgolny },
  );

  // Propsy do OlMap — memo, żeby nie tworzyć nowych referencji co render.
  // Wcześniej liczone inline IIFE przy propsach, z zduplikowanym lookupem
  // activeProcedure/slugProcedure. planContext: warstwa POG tylko dla
  // procedur typu '3.1 plan ogólny gminy' (backend zwraca 404 gdy planu
  // nie wgrano — warstwa zostaje pusta).
  const planContext = useMemo(
    () =>
      activeProcedure && isActivePlanOgolny
        ? { tenantId: tenant, procedureId: activeProcedure.id }
        : undefined,
    [activeProcedure, isActivePlanOgolny, tenant],
  );
  const rasterCogUrls = useMemo(
    () =>
      !isActivePlanOgolny &&
      activeProcedure &&
      activeRasterMetaQ.data?.s3Key &&
      activeRasterMetaQ.data?.status === "ready"
        ? [
            `${import.meta.env.VITE_API_URL || ""}/tenant/${tenant}/plan/${encodeURIComponent(activeProcedure.id)}/raster.cog.tif`,
          ]
        : undefined,
    [isActivePlanOgolny, activeProcedure, activeRasterMetaQ.data, tenant],
  );
  // Na landing: domyślnie pokazuj podgląd pierwszej aktywnej procedury po
  // prawej stronie. Jeśli user kliknie na kartę innej — aktualizujemy preview.
  useEffect(() => {
    if (procedureSlug) return;
    if (previewProcedureId && procedures.some((p) => p.id === previewProcedureId)) return;
    const fallback = pickDefaultProcedure(procedures);
    if (fallback) setPreviewProcedureId(fallback.id);
  }, [procedures, procedureSlug, previewProcedureId]);

  const previewProcedure = procedures.find((p) => p.id === previewProcedureId) ?? null;

  // Mierzymy wysokość lewej kolumny (listy procedur + konsultacje) i sprawdzamy
  // czy tresc "Ogłoszenia" przekracza tę wysokość. Jeśli tak, pokazujemy
  // przycisk "Pokaż całość". Re-run przy zmianie procedury lub rozmiaru okna.
  useLayoutEffect(() => {
    const measure = () => {
      if (leftColumnRef.current) {
        setLeftColumnHeight(leftColumnRef.current.offsetHeight);
      }
      if (announcementContentRef.current && leftColumnRef.current) {
        const contentH = announcementContentRef.current.scrollHeight;
        // ~80 px rezerwy na padding karty + nagłówek "Ogłoszenie"
        const availableH = leftColumnRef.current.offsetHeight - 80;
        setAnnouncementOverflows(contentH > availableH);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [previewProcedureId, previewProcedure?.payload, procedures.length]);

  const loadSlots = (sessionId: string) => {
    setSelectedSession(selectedSession === sessionId ? null : sessionId);
    setSelectedSlot(null);
  };

  // UWAGA: `handleBook` musi być zadeklarowany PRZED `renderBookingPanel`,
  // bo `renderBookingPanel` go zamyka w closure'ze. W bundlu produkcyjnym
  // Vite/Rollup hoistuje deklaracje w nieokreślonej kolejności — jeśli
  // `renderBookingPanel` jest zdefiniowane wcześniej, dostaniemy
  // "Cannot access 'handleBook' before initialization" (TDZ) przy
  // pierwszym renderze (co już raz złapaliśmy w c661e70).
  const handleBook = async () => {
    if (!selectedSession || !selectedSlot || !bookingForm.name || !bookingForm.email) return;
    setBookingStatus(null);
    try {
      await bookMutation.mutateAsync({ ...bookingForm, slotTime: selectedSlot.time, consultantNumber: selectedSlot.consultantNumber });
      setBookingStatus("success");
      setSelectedSlot(null);
      setBookingForm({ name: "", email: "", phone: "" });
      slotsQuery.refetch();
    } catch (err: any) {
      setBookingStatus(err?.response?.data?.message || "Wystąpił błąd");
    }
  };

  // Panel rezerwacji wizyty — tabela wolnych slotów + formularz danych +
  // status + opcjonalna sekcja "Brak terminów / powiadom mnie".
  // Używany w obu wariantach widoku publicznego (landing 2-kolumnowy
  // i single-procedure). Funkcja zwraca JSX, korzysta z parent state
  // (selectedSession, selectedSlot, slots, bookingForm, ...) — dlatego
  // nie wyciągamy jej do osobnego komponentu, byłoby ~10 propsów.
  const renderBookingPanel = () => (
    <div className="mt-3 border-t pt-3">
      {loadingSlots ? <p className="text-base">Ładowanie…</p> : (
        <>
          <table className="w-full border-collapse text-sm mb-3">
            <thead>
              <tr>
                {Array.from({ length: numConsultants }, (_, i) => (
                  <th key={i} className="text-center p-1 border-b font-semibold text-gray-700">
                    St.{i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uniqueTimes.map((time) => (
                <tr key={time}>
                  {Array.from({ length: numConsultants }, (_, cIdx) => {
                    const cn = cIdx + 1;
                    const slot = slots.find((s) => s.time === time && s.consultantNumber === cn);
                    const av = slot?.available ?? false;
                    const sel = selectedSlot?.time === time && selectedSlot?.consultantNumber === cn;
                    return (
                      <td key={cIdx} className="p-1 text-center">
                        <button
                          disabled={!av}
                          onClick={(e) => { e.stopPropagation(); setSelectedSlot({ time, consultantNumber: cn }); }}
                          className={`w-full py-2 rounded font-mono text-sm font-medium ${
                            sel
                              ? ""
                              : av
                              ? "bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer"
                              : "bg-red-50 text-red-400 line-through cursor-not-allowed"
                          }`}
                          style={sel ? theme.selectedSlot : undefined}
                          title={av ? `Zarezerwuj ${time}` : `${time} — zajęty`}>
                          {time}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {selectedSlot && (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <p className="text-base font-medium">Termin: <span style={theme.accentText}>{selectedSlot.time}</span></p>
              <input type="text" placeholder="Imię i nazwisko *" value={bookingForm.name} onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })} className="w-full px-3 py-2 border rounded text-base" />
              <input type="email" placeholder="Email *" value={bookingForm.email} onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })} className="w-full px-3 py-2 border rounded text-base" />
              <input type="tel" placeholder="Telefon (opcja)" value={bookingForm.phone} onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })} className="w-full px-3 py-2 border rounded text-base" />
              <Button onClick={handleBook} disabled={!bookingForm.name || !bookingForm.email} className="w-full">Zarezerwuj wizytę</Button>
            </div>
          )}
          {bookingStatus === "success" && <p className="text-base text-green-600 mt-2">Zapisano! Sprawdź email.</p>}
          {bookingStatus && bookingStatus !== "success" && <p className="text-base text-red-600 mt-2">{bookingStatus}</p>}
          {!hasAvailableSlots && (
            <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-3" onClick={(e) => e.stopPropagation()}>
              <p className="text-sm text-orange-800 mb-2">Brak wolnych terminów.</p>
              <div className="flex gap-1">
                <input type="email" placeholder="Email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} className="flex-1 px-2 py-1.5 border rounded text-base" />
                <Button variant="outline" size="sm" disabled={!notifyEmail} onClick={async () => {
                  try { await notifyMutation.mutateAsync({ email: notifyEmail }); setNotifyStatus("success"); setNotifyEmail(""); }
                  catch { setNotifyStatus("error"); }
                }}>Powiadom</Button>
              </div>
              {notifyStatus === "success" && <p className="text-sm text-green-600">Zapisano!</p>}
            </div>
          )}
        </>
      )}
    </div>
  );

  // Map: parcel click. W consultantMode delegujemy do rodzica (ConsultantMapPage)
  // który otwiera modal notatki projektanta, a działka trafia też na listę
  // w panelu — z udziałem przeznaczeń terenu jak u mieszkańca.
  const handleParcelClick = async (info: ParcelInfo | null) => {
    if (!info?.id) return;
    if (consultantMode) {
      logSearch({
        kind: "parcel_click",
        query: info.id,
        result: [info.obreb && `obręb ${info.obreb}`, info.commune].filter(Boolean).join(", ") || undefined,
        resultCount: 1,
      });
      onConsultantParcelClick?.(info.id);
    }
    if (selectedParcels.some((p) => p.id === info.id)) return;
    try {
      const geometry = await getParcelGeometryById(info.id);
      setSelectedParcels((prev) => [...prev, { id: info.id, geometry, info }]);
    } catch (err) { console.error("Failed to fetch parcel geometry:", err); }
  };

  const { mutateAsync: createDraft } = useCreateApplicationDraftMutation();

  // Navigate to application form with selected parcels.
  //
  // Przed redirectem zapisujemy DRAFT w bazie — admin widzi w panelu
  // listę "rozpoczętych" wniosków/uwag (tabela `application_drafts`)
  // wraz z parcelIds, IP, user-agentem. Dzięki temu jest informacja
  // że ktoś zaczął procedurę składania, nawet jeśli nie ukończył
  // formularza. Throttle backendu 3 req/10s blokuje spam.
  //
  // sessionStorage zapisuje też `applicationDraftId` — może być użyte
  // przez CreateApplicationForm do skojarzenia finalnego submit-u
  // z istniejącym draft-em (poza scope MVP, ale bramka jest gotowa).
  //
  // Best-effort: jeśli draft POST padnie (CORS / throttle / network),
  // logujemy do konsoli i kontynuujemy redirect — nie blokujemy
  // użytkownika przed wypełnianiem formularza.
  const goToApplication = async () => {
    if (!activeProcedure || selectedParcels.length === 0) return;
    sessionStorage.setItem(
      "selectedParcels",
      JSON.stringify(selectedParcels.map((p) => ({ id: p.id, geometry: p.geometry }))),
    );
    try {
      const draft = await createDraft({
        tenantId: tenant,
        formId: activeProcedure.id,
        parcelIds: selectedParcels.map((p) => p.id),
      });
      sessionStorage.setItem("applicationDraftId", draft.id);
      // eslint-disable-next-line no-console
      console.log("[application-draft] utworzono", draft);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[application-draft] nie udało się zapisać draftu (best-effort):", err);
    }
    window.location.href = `/${tenant}/${activeProcedure.id}/application`;
  };

  const handleParcelSearch = async () => {
    if (!parcelSearchObreb || !parcelSearchNumer.trim()) return;
    const numer = parcelSearchNumer.trim();
    // parcelSearchObreb from select contains full precinct TERYT like "126201_1.0001"
    const fullId = `${parcelSearchObreb}.${numer}`;
    setParcelSearchLoading(true);
    const searchQuery = `${parcelSearchObrebInput.trim() || parcelSearchObreb} / ${numer}`;
    try {
      const geometry = await getParcelGeometryById(fullId);
      logSearch({ kind: "parcel", query: searchQuery, result: fullId, resultCount: 1 });
      setSelectedParcels((prev) => {
        if (prev.some((p) => p.id === fullId)) return prev;
        return [...prev, { id: fullId, geometry }];
      });
      const geo = Array.isArray(geometry) ? geometry[0] : geometry;
      if (geo?.center) setLatLng(geo.center);
      // Obręb zostaje wybrany — zmienia się tylko numer działki. Czyszczenie
      // obrębu: przycisk X w polu obrębu.
      setParcelSearchNumer("");
      parcelSearchNumerRef.current?.focus();
    } catch {
      logSearch({ kind: "parcel", query: searchQuery, result: fullId, resultCount: 0 });
      alert(`Nie znaleziono działki: ${fullId}`);
    }
    setParcelSearchLoading(false);
  };

  // Address search (Nominatim) — kontekstualizowane do gminy.
  // Dodajemy nazwę gminy do query żeby Nominatim preferował lokalne wyniki
  // (np. „Lwowska 1" → „Lwowska 1, Nowy Sącz" zamiast Lwowska w Krakowie).
  const handleAddressSearch = async () => {
    if (!addressQuery.trim()) return;
    const municipalityName = tenantData?.displayName || "";
    const contextualQuery = municipalityName
      ? `${addressQuery.trim()}, ${municipalityName}`
      : addressQuery.trim();
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(contextualQuery)}&format=jsonv2&countrycodes=pl&limit=5`,
      );
      const results = await res.json();
      setAddressResults(results);
      logSearch({ kind: "address", query: addressQuery, resultCount: Array.isArray(results) ? results.length : 0 });
    } catch {
      setAddressResults([]);
      logSearch({ kind: "address", query: addressQuery, result: "błąd wyszukiwarki adresów", resultCount: 0 });
    }
  };

  // Autocomplete po obrębach — filtrujemy precincts po wpisanym tekście adresowym.
  // Defensywnie guardujemy label/value na wypadek gdyby useUldk zwrócił wpis
  // z brakującym polem (np. niepełna linia z ULDK API).
  const precinctSuggestions = useMemo(() => {
    const q = addressQuery.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    if (!Array.isArray(precincts)) return [];
    return precincts
      .filter((p) => {
        const label = (p?.label ?? "").toLowerCase();
        const value = p?.value ?? "";
        return label.includes(q) || value.includes(q);
      })
      .slice(0, 8);
  }, [addressQuery, precincts]);

  // Autocomplete obrębu w sidebar "Wyszukaj działkę".
  //
  // Format value z ULDK to TERYT `WWPPGG_T.OOOO` (np. `126201_1.0001`).
  // Filtrowanie po CAŁYM value jest mylące — np. query "12" matchuje
  // "126201_1.0002" tylko dlatego, że prefiks gminy zaczyna się na "12".
  // Dlatego porównujemy TYLKO ostatni segment (numer obrębu, ≤ 4 cyfr)
  // lub label (nazwę obrębu).
  //
  // Zera wiodące w numerze są ignorowane — "1" matchuje "001", "01", "1";
  // "12" nie matchuje "0002" (bo po normalizacji "0002"→"2", a "2".startsWith("12")=false).
  const normalizeDigits = (s: string): string =>
    s.replace(/(?<=^|\D)0+(\d)/g, "$1").toLowerCase();

  const extractPrecinctNumber = (value: string): string => {
    // `126201_1.0001` → `0001`; jeśli brak kropki, zwraca cały string.
    const parts = (value || "").split(".");
    return parts[parts.length - 1] || "";
  };

  const obrebSuggestions = useMemo(() => {
    const raw = parcelSearchObrebInput.trim();
    if (raw.length === 0) return [];
    if (!Array.isArray(precincts)) return [];
    const isNumericQuery = /^\d+$/.test(raw);
    const q = normalizeDigits(raw);
    return precincts
      .filter((p) => {
        const lbl = (p?.label ?? "").toLowerCase();
        const num = normalizeDigits(extractPrecinctNumber(p?.value ?? ""));
        if (isNumericQuery) {
          // Query numeryczne → porównuj TYLKO z numerem obrębu (prefix match
          // po normalizacji zer wiodących). "1"→matchuje 001,01,1; "12"→12 tylko.
          return num.startsWith(q);
        }
        // Query tekstowe → match po nazwie (substring, bo nazwy mogą być długie).
        return lbl.includes(q);
      })
      .slice(0, 12);
  }, [parcelSearchObrebInput, precincts]);

  if (tenantQuery.isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Nie znaleziono gminy</h1>
          <Link to="/" className="text-blue-600 underline">Wróć na stronę główną</Link>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  // LANDING GMINY (brak slug procedury w URL) — prosty widok
  // z listą konsultacji + kartami procedur. Bez mapy i panelu.
  // W consultantMode pomijamy ten landing — konsultant ma już wybraną
  // procedurę przez `procedureIdOverride` (slugProcedure jest ustawione
  // mimo że procedureSlug z URL jest undefined), więc lecimy do renderu
  // mapy poniżej.
  // ────────────────────────────────────────────────────────────
  if (!procedureSlug && !consultantMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header — kolor rozciąga się na pełną szerokość ekranu, treść
            dopasowana do lewej krawędzi (żeby herb + nazwa gminy zaczynały się
            od lewego skraju — zgodnie z treścią lewej kolumny pod spodem). */}
        <div className="shrink-0" style={theme.header}>
          <div className="w-full py-4 px-6 md:px-10 flex items-center gap-4 min-h-[80px]">
            {tenantPayload?.logo && (
              <img
                src={tenantPayload.logo}
                alt={`Herb gminy ${tenantData?.displayName || tenant}`}
                className="h-16 w-auto object-contain shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-3xl font-bold truncate">{tenantData?.displayName || tenant}</h1>
              <p className="text-sm mt-0.5" style={theme.headerSubtitle}>
                Portal konsultacji społecznych i wniosków
              </p>
            </div>
            {/* CTA w górnym pasku — szybki dostęp do systemu składania wniosków */}
            {(() => {
              const firstActive = procedures.find((p) => isProcedureActive(p));
              if (!firstActive) return null;
              return (
                <div className="hidden md:flex items-center gap-2 shrink-0">
                  {(firstActive.payload as any)?.survey?.enabled && (
                    <Link
                      to="/$tenant/$formId/geoankieta"
                      params={{ tenant, formId: firstActive.id }}
                      className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors border border-white/30">
                      <ClipboardList className="h-4 w-4" />
                      <span>Wypełnij geoankietę</span>
                    </Link>
                  )}
                  <Link
                    to="/$tenant/$procedureSlug"
                    params={{ tenant, procedureSlug: firstActive.name }}
                    className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors border border-white/30">
                    <span>{pageSettings?.applicationType === "uwaga" ? "Złóż uwagę do projektu planu" : "Złóż wniosek do planu"}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Główna zawartość — dwukolumnowy layout na szerokich ekranach.
            Pelna szerokosc ekranu (bez max-w), lewa kolumna ~400px, prawa
            rozciagnieta do konca. items-start zeby wysokosc kolumn byla
            naturalna (wg zawartosci), a nie wyciagana do najwiekszej. */}
        <div className="flex-1 w-full px-6 md:px-10 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(340px,_440px)_1fr] gap-8 items-start">

            {/* LEWA KOLUMNA */}
            <div className="space-y-6" ref={leftColumnRef}>

              {/* Sekcja "Konsultacje społeczne" — jeśli są zaplanowane.
                  Po revisit zwinięta (user już widział kontekst, chce szybciej
                  do akcji). Na pierwszej wizycie — rozwinięta. */}
              {((pageSettings?.consultationInfo && pageSettings.consultationInfo.trim().length > 0) || sessions.length > 0) && (
                <section>
                  <button
                    type="button"
                    onClick={() => setConsultationsCollapsed((c) => !c)}
                    className="flex items-center gap-2 w-full text-left mb-3 group">
                    <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${!consultationsCollapsed ? "rotate-90" : ""}`} />
                    <h2 className="text-xl font-bold" style={theme.sectionTitle}>
                      Konsultacje społeczne
                    </h2>
                  </button>
                  {!consultationsCollapsed && (
                    <>
                  {pageSettings?.consultationInfo && pageSettings.consultationInfo.trim().length > 0 && (
                    <div
                      className="prose prose-sm max-w-none text-gray-700 mb-3 [&_a]:underline [&_a]:text-blue-700"
                      dangerouslySetInnerHTML={{ __html: consultationInfoHtml }}
                    />
                  )}
                  {/* Lista sesji (Dyżur projektanta, Spotkanie otwarte) została
                      przeniesiona do kart procedur poniżej — rozwija się
                      przyciskiem "Umów wizytę na dyżur projektanta" w każdej
                      karcie procedury. Tutaj zostaje tylko ogólny opis
                      konsultacji. */}
                    </>
                  )}
                </section>
              )}

              {/* Sekcja procedur */}
              <section>
                <h2 className="text-xl font-bold mb-3" style={theme.sectionTitle}>
                  {(() => {
                    const letter = pageSettings?.applicationType === "uwaga" ? "złóż uwagę" : "złóż wniosek";
                    const anySurvey = procedures.some((p) => (p.payload as any)?.survey?.enabled);
                    return anySurvey
                      ? `Procedury — ${letter}, wypełnij geoankietę lub umów wizytę na dyżurze projektanta`
                      : `Procedury — ${letter} lub umów wizytę na dyżurze projektanta`;
                  })()}
                </h2>
                {(() => {
                  const now = new Date();
                  const activeProcedures = procedures.filter((p) => {
                    const from = new Date(p.activeFrom);
                    const to = new Date(p.activeTo);
                    return now >= from && now <= to;
                  });
                  const archivedProcedures = procedures.filter((p) => {
                    const to = new Date(p.activeTo);
                    return now > to;
                  });
                  const upcomingProcedures = procedures.filter((p) => {
                    const from = new Date(p.activeFrom);
                    return now < from;
                  });

                  const renderProcedureCard = (proc: typeof procedures[number], status: "active" | "archived" | "upcoming") => {
                    const from = new Date(proc.activeFrom);
                    const to = new Date(proc.activeTo);
                    const fromStr = from.toLocaleDateString("pl-PL");
                    const toStr = to.toLocaleDateString("pl-PL");
                    const badge =
                      status === "active"
                        ? { label: "Aktywna", cls: "bg-green-100 text-green-800" }
                        : status === "archived"
                        ? { label: "Archiwalna", cls: "bg-gray-100 text-gray-600" }
                        : { label: "Wkrótce", cls: "bg-amber-100 text-amber-800" };
                    const isPreview = previewProcedureId === proc.id;
                    return (
                      <div
                        key={proc.id}
                        onClick={() => { setPreviewProcedureId(proc.id); setAnnouncementExpanded(false); }}
                        className={`cursor-pointer bg-white border-2 rounded-lg p-4 shadow-sm transition-all ${
                          isPreview ? "shadow-md" : "border-gray-200 hover:border-gray-400 hover:shadow-md"
                        }`}
                        style={isPreview ? { borderColor: (theme.accentText as any).color } : undefined}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-semibold text-base" style={theme.sectionTitle}>
                            {proc.displayName}
                          </h3>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded shrink-0 whitespace-nowrap ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </div>
                        {proc.description && (
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2">{proc.description}</p>
                        )}
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                          <Calendar className="h-3 w-3" />
                          <span>{fromStr} — {toStr}</span>
                        </div>
                        {status === "upcoming" && isAdminHere && (
                          <AdminTestPanel
                            tenant={tenant}
                            formId={proc.id}
                            from={from}
                            letter={pageSettings?.applicationType === "uwaga" ? "uwagi" : "wniosku"}
                            surveyEnabled={!!(proc.payload as any)?.survey?.enabled}
                            compact
                          />
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Dwie równorzędne akcje główne: pismo (uwaga/wniosek) i geoankieta.
                              Geoankieta jest osobną formą konsultacji (art. 8i ust. 1 pkt 4),
                              więc ma taką samą wagę wizualną jak składanie uwag. */}
                          <Link
                            to="/$tenant/$procedureSlug"
                            params={{ tenant, procedureSlug: proc.name }}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm">
                            <MapPin className="h-4 w-4" />
                            <span>{pageSettings?.applicationType === "uwaga" ? "Złóż uwagę do projektu planu" : "Złóż wniosek do planu"}</span>
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                          {(proc.payload as any)?.survey?.enabled && (
                            <Link
                              to="/$tenant/$formId/geoankieta"
                              params={{ tenant, formId: proc.id }}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow-sm">
                              <ClipboardList className="h-4 w-4" />
                              <span>Wypełnij geoankietę</span>
                              <ChevronRight className="h-4 w-4" />
                            </Link>
                          )}
                          {sessions.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedSessionsProcedure((cur) => (cur === proc.id ? null : proc.id));
                              }}
                              className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                              <Calendar className="h-4 w-4" />
                              <span>
                                {expandedSessionsProcedure === proc.id
                                  ? "Ukryj listę wizyt"
                                  : "Umów wizytę na dyżur projektanta"}
                              </span>
                            </button>
                          )}
                        </div>
                        {/* Rozwijana lista sesji konsultacji (dyżury projektanta,
                            spotkania otwarte). Sesje są wspólne dla całej gminy
                            (nie ma jeszcze w bazie pola formId per sesja), więc
                            ten sam zestaw rozwija się pod każdą procedurą. */}
                        {expandedSessionsProcedure === proc.id && sessions.length > 0 && (
                          <div
                            className="mt-3 space-y-3"
                            onClick={(e) => e.stopPropagation()}>
                            {sessionsByKind.map(([kindLabel, items]) => (
                              <div key={kindLabel}>
                                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2 pb-1 border-b border-gray-200">
                                  {kindLabel}
                                </h4>
                                <div className="space-y-2">
                                  {items.map((session) => {
                                    const date = new Date(session.consultationDate).toLocaleDateString("pl-PL", {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                    });
                                    const allowBooking = (session as any).allowBooking !== false;
                                    const isSelected = selectedSession === session.id;
                                    return (
                                      <div
                                        key={session.id}
                                        onClick={() => { if (allowBooking) loadSlots(session.id); }}
                                        className={`bg-white border rounded-lg p-4 shadow-sm transition-colors ${
                                          isSelected ? "border-blue-500 ring-1 ring-blue-300" : "border-gray-200"
                                        } ${allowBooking ? "cursor-pointer hover:border-gray-400" : ""}`}>
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-0.5">
                                              <Calendar className="h-3 w-3" />
                                              <span>{date}</span>
                                              <span>·</span>
                                              <span>{session.startTime}–{session.endTime}</span>
                                            </div>
                                            <h5 className="font-semibold text-sm text-gray-900">{session.title}</h5>
                                            {session.description && (
                                              <p className="text-xs text-gray-600 mt-0.5">{session.description}</p>
                                            )}
                                          </div>
                                          {allowBooking ? (
                                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 shrink-0 whitespace-nowrap">
                                              {isSelected ? "Zwiń" : "Umów wizytę"}
                                            </span>
                                          ) : (
                                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0 whitespace-nowrap">
                                              Bez rezerwacji
                                            </span>
                                          )}
                                        </div>
                                        {isSelected && allowBooking && renderBookingPanel()}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  };

                  if (procedures.length === 0) {
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                        <p className="text-sm text-gray-500">Brak procedur planistycznych.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {/* Aktywne */}
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          Aktywne {activeProcedures.length > 0 && `(${activeProcedures.length})`}
                        </h3>
                        {activeProcedures.length === 0 ? (
                          <p className="text-xs text-gray-400 italic">Brak aktywnych procedur.</p>
                        ) : (
                          <div className="space-y-2">
                            {activeProcedures.map((p) => renderProcedureCard(p, "active"))}
                          </div>
                        )}
                      </div>

                      {/* Nadchodzące */}
                      {upcomingProcedures.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                            Wkrótce ({upcomingProcedures.length})
                          </h3>
                          <div className="space-y-2">
                            {upcomingProcedures.map((p) => renderProcedureCard(p, "upcoming"))}
                          </div>
                        </div>
                      )}

                      {/* Archiwalne — domyślnie zwinięte */}
                      {archivedProcedures.length > 0 && (
                        <details className="group">
                          <summary className="cursor-pointer list-none flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700">
                            <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                            <span>Archiwalne ({archivedProcedures.length})</span>
                          </summary>
                          <div className="space-y-2 mt-2">
                            {archivedProcedures.map((p) => renderProcedureCard(p, "archived"))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })()}
              </section>
            </div>

            {/* PRAWA KOLUMNA: opis + pliki wybranej procedury */}
            <div className="space-y-4 min-w-0">
              {!previewProcedure ? (
                <div className="bg-white border border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <p className="text-sm text-gray-500">Wybierz procedurę z listy obok, aby zobaczyć szczegóły.</p>
                </div>
              ) : (
                <>
                  {/* Naglowek procedury (tytul + daty + opis + przycisk "Otworz strone")
                      przeniesiony do karty w lewej kolumnie — nie duplikujemy go tu.
                      W prawej kolumnie pokazujemy tylko tresc: ogloszenie i pliki. */}

                  {/* Duży przycisk "Przejdź do mapy" — główne wejście mieszkańca:
                      na stronie procedury jest mapa z rysunkiem planu, wyszukiwarka
                      działki, karta przeznaczeń i składanie pisma. */}
                  <Link
                    to="/$tenant/$procedureSlug"
                    params={{ tenant, procedureSlug: previewProcedure.name }}
                    className="group flex items-center gap-4 rounded-xl bg-green-600 px-5 py-4 text-white shadow-md transition-all hover:bg-green-700 hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-green-300">
                    <MapIcon className="h-10 w-10 shrink-0" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xl sm:text-2xl font-bold leading-tight">Przejdź do mapy projektu planu</div>
                      <div className="text-sm sm:text-base mt-0.5" style={{ opacity: 0.9 }}>
                        Znajdź swoją działkę, sprawdź jej przeznaczenie i{" "}
                        {pageSettings?.applicationType === "uwaga" ? "złóż uwagę" : "złóż wniosek"}.
                      </div>
                    </div>
                    <ChevronRight className="h-8 w-8 shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>

                  {/* Ogłoszenie — skrócone do wysokości lewej kolumny z gradient fade
                      i przyciskiem "Pokaż całość". Rozwinięte pokazuje całą treść. */}
                  {(previewProcedure.payload as any)?.consultationDescription && (
                    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                      <h3 className="text-lg font-semibold mb-3" style={theme.sectionTitle}>Ogłoszenie</h3>
                      <div
                        className="relative"
                        style={
                          !announcementExpanded && announcementOverflows && leftColumnHeight
                            ? { maxHeight: Math.max(leftColumnHeight - 120, 300), overflow: "hidden" }
                            : undefined
                        }>
                        <div
                          ref={announcementContentRef}
                          className="text-sm text-gray-700 prose prose-sm max-w-none [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml((previewProcedure.payload as any).consultationDescription) }}
                        />
                        {/* Fade-out gdy treść jest skrócona */}
                        {!announcementExpanded && announcementOverflows && (
                          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                        )}
                      </div>
                      {announcementOverflows && (
                        <button
                          type="button"
                          onClick={() => setAnnouncementExpanded(!announcementExpanded)}
                          className="mt-3 inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors">
                          {announcementExpanded ? "Zwiń ogłoszenie" : "Pokaż całość"}
                          <ChevronRight
                            className={`h-4 w-4 transition-transform ${announcementExpanded ? "-rotate-90" : "rotate-90"}`}
                          />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Pliki do pobrania, pogrupowane w sekcje + ZIP kompletu */}
                  <ProcedureDownloads
                    tenant={tenant}
                    procedureId={previewProcedure.id}
                    files={previewProcedure.files}
                    payload={previewProcedure.payload}
                    sectionTitleStyle={theme.sectionTitle}
                  />
                </>
              )}
            </div>

          </div>

          {/* Logowanie admin / konsultant */}
          <div className="text-center pt-4 border-t border-gray-200">
            <Link
              to="/$tenant/login"
              params={{ tenant }}
              className="text-sm text-gray-400 hover:text-gray-600">
              Logowanie admin / konsultant
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  // STRONA POJEDYNCZEJ PROCEDURY — pełny layout z mapą i panelem
  // ────────────────────────────────────────────────────────────
  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden">
      {/* Header */}
      <div className="py-3 px-6 flex justify-between items-center gap-4 shrink-0 min-h-[72px]" style={theme.header}>
        <div className="flex items-center gap-4 min-w-0">
          {tenantPayload?.logo && (
            <img
              src={tenantPayload.logo}
              alt={`Herb gminy ${tenantData?.displayName || tenant}`}
              className="h-14 w-auto object-contain shrink-0"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">
              {tenantData?.displayName || tenant}
              {slugProcedure && (
                <span className="font-normal opacity-80"> · {slugProcedure.displayName}</span>
              )}
            </h1>
            <p className="text-sm" style={theme.headerSubtitle}>
              Strona procedury planistycznej
            </p>
          </div>
        </div>
        {/* Link powrotny do landing gminy — pominięty w consultantMode
            (konsultant wraca przez własne menu sidebara). */}
        {!consultantMode && (
          <Link
            to="/$tenant"
            params={{ tenant }}
            className="text-sm underline opacity-80 hover:opacity-100 shrink-0"
            style={{ color: theme.header.color }}>
            ← Wróć do listy
          </Link>
        )}
      </div>

      {/* Main content — na desktop: resizable side-by-side (left panel + map).
          Na mobile: mapa zajmuje cały ekran, panel jest wysuwany od dołu. */}
      {isMobile ? (
        /* ─── MOBILE LAYOUT ─── */
        <div className="flex-1 flex flex-col relative overflow-hidden">
          {/* Mapa na pełen ekran */}
          {renderMapContent()}
          {/* Toggle panelu */}
          <button
            type="button"
            onClick={() => setMobilePanelOpen((o) => !o)}
            className="shrink-0 bg-white border-t border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 flex items-center justify-between">
            <span>{mobilePanelOpen ? "Ukryj panel" : "Pokaż wyszukiwarkę i działki"}</span>
            <ChevronRight className={`h-5 w-5 transition-transform ${mobilePanelOpen ? "-rotate-90" : "rotate-90"}`} />
          </button>
          {mobilePanelOpen && (
            <div className="max-h-[50vh] overflow-y-auto bg-gray-50 p-4 space-y-4 border-t border-gray-200">
              {renderLeftPanelContent()}
            </div>
          )}
        </div>
      ) : (
        /* ─── DESKTOP LAYOUT ─── */
        <ResizablePanelGroup direction="horizontal" className="flex-1">
          {/* LEFT PANEL — w consultantMode startujemy z węższym panelem
              (~18%) bo konsultant nie potrzebuje sekcji "Złóż wniosek" /
              "Wybrane działki" — sidebar pokazuje tylko wyszukiwarki.
              minSize 14% pozwala na maksymalne zwężenie pod mapę. */}
          <ResizablePanel
            defaultSize={consultantMode ? 18 : 28}
            minSize={consultantMode ? 14 : 18}
            maxSize={60}
            className="bg-gray-50">
            <div className="h-full overflow-y-auto p-6 space-y-6">
              {renderLeftPanelContent()}
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* MAP */}
          <ResizablePanel defaultSize={consultantMode ? 82 : 72} minSize={40}>
            {renderMapContent()}
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );

  // ── Render functions ───────────────────────────────────────────────
  // Extracted so the same content is used in both mobile (vertical flex)
  // and desktop (ResizablePanelGroup) layouts.

  function renderLeftPanelContent() {
    return (
      <>

          {/* Komunikat gdy slug nie pasuje do żadnej procedury */}
          {slugMissingMatch && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
              Nie znaleziono procedury o adresie <strong>{procedureSlug}</strong> w tej gminie. Poniżej lista wszystkich dostępnych procedur.
            </div>
          )}

          {/* Opis konsultacji i Pliki do pobrania sa widoczne na landing
              gminy (/{tenant}) — na stronie pojedynczej procedury panel
              lewy jest zarezerwowany dla wyszukiwania adresu/dzialek i
              wyboru dzialek do wniosku. */}

          {/* Konsultacje społeczne — tylko opis tekstowy z pola
              `consultationInfo`. Lista sesji + tabela slotów rezerwacji
              jest dostępna wyłącznie na stronie głównej gminy (landing
              2-kolumnowy), żeby strona pojedynczej procedury skupiała
              użytkownika na akcji „Złóż wniosek/uwagę" — a nie na
              alternatywnej czynności „Umów wizytę". */}
          {(() => {
            const procConsult = (activeProcedure?.payload as any)?.consultationDescription || "";
            const consultHtml = procConsult.trim()
              ? sanitizeHtml(/<\/?[a-z][\s\S]*?>/i.test(procConsult) ? procConsult : procConsult.replace(/\n/g, "<br/>"))
              : consultationInfoHtml;
            const hasConsult = procConsult.trim() || (pageSettings?.consultationInfo && pageSettings.consultationInfo.trim().length > 0);
            if (!hasConsult) return null;
            return (
            <div className="border-b pb-4">
              <button
                type="button"
                onClick={() => setConsultationInfoCollapsed((c) => !c)}
                className="flex items-center gap-2 w-full text-left mb-3 group">
                <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${!consultationInfoCollapsed ? "rotate-90" : ""}`} />
                <h2 className="text-lg font-bold" style={theme.sectionTitle}>Konsultacje społeczne</h2>
              </button>
              <div
                className={`prose prose-base max-w-none text-gray-600 [&_a]:underline [&_a]:text-blue-700 ${consultationInfoCollapsed ? "line-clamp-3" : ""}`}
                dangerouslySetInnerHTML={{ __html: consultHtml }}
              />
            </div>
            );
          })()}

          {/* Wyszukaj adres lub działkę — KROK 1, wyróżniony ramką w kolorze
              gminy, żeby mieszkaniec od razu wiedział, od czego zacząć. */}
          <div className="rounded-xl border-2 p-4 shadow-md" style={theme.selectedItem}>
            <div className="flex items-start gap-3 mb-4">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-bold shadow-sm"
                style={theme.header}
                aria-hidden="true">
                1
              </span>
              <div>
                <h2 className="text-xl font-bold leading-tight" style={theme.sectionTitle}>
                  Zacznij tutaj: znajdź swoją działkę
                </h2>
                <p className="text-sm text-gray-700 mt-1">
                  Wyszukaj adres albo obręb i numer działki — lub kliknij działkę na mapie.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-base font-semibold text-gray-900">Wyszukaj adres lub miejsce</h3>
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-500 text-xs font-bold cursor-help shrink-0"
                title={'Podaj adres wraz z nazwą miejscowości, np. Lwowska 1, Nowy Sącz. Wyniki są ograniczone do gminy.'}
              >{"?"}</span>
            </div>
            <div className="flex gap-1 mb-2">
              <input
                type="text"
                placeholder={tenantData?.displayName ? `np. Lwowska 1, ${tenantData.displayName}` : "np. ul. Lwowska 1"}
                value={addressQuery}
                onChange={(e) => { setAddressQuery(e.target.value); setAddressResults([]); }}
                onKeyDown={(e) => e.key === "Enter" && handleAddressSearch()}
                className="flex-1 min-w-0 px-3 py-2.5 border rounded bg-white text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <Button size="default" variant="outline" onClick={handleAddressSearch}>
                <Search className="h-5 w-5" />
              </Button>
            </div>
            {/* Autocomplete: sugestie obręb (gdy tekst pasuje do nazwy obrębu) */}
            {precinctSuggestions.length > 0 && addressResults.length === 0 && (
              <div className="border rounded max-h-36 overflow-y-auto mb-2 bg-white">
                <div className="px-3 py-1 text-xs text-gray-400 border-b">Obręby:</div>
                {precinctSuggestions.map((p) => (
                  <div
                    key={p.value}
                    className="text-sm py-2 px-3 cursor-pointer hover:bg-blue-50 border-b border-gray-50 flex items-center gap-2"
                    onClick={async () => {
                      logSearch({ kind: "precinct", query: addressQuery, result: `${p.label} (${p.value})` });
                      try {
                        const geo = await getRegionGeometryById(p.value);
                        const g = Array.isArray(geo) ? geo[0] : geo;
                        if (g?.center) setLatLng(g.center);
                      } catch { /* ignore */ }
                      setParcelSearchObreb(p.value);
                      setParcelSearchObrebInput(p.label);
                      setAddressQuery("");
                    }}>
                    <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span>{p.label} <span className="text-gray-400">({p.value})</span></span>
                  </div>
                ))}
              </div>
            )}
            {addressResults.length > 0 && (
              <div className="border rounded max-h-36 overflow-y-auto mb-2">
                {addressResults.map((r) => (
                  <div key={r.place_id} className="text-sm py-2 px-3 cursor-pointer hover:bg-blue-50 border-b border-gray-50"
                    onClick={() => {
                      logSearch({ kind: "address_select", query: addressQuery, result: r.display_name });
                      setLatLng({ lat: Number(r.lat), lng: Number(r.lon) });
                      setAddressResults([]);
                      setAddressQuery("");
                    }}>
                    {r.display_name}
                  </div>
                ))}
              </div>
            )}

            <h3 className="text-base font-semibold text-gray-900 mt-4 mb-1">Wyszukaj działkę</h3>
            <p className="text-sm text-gray-600 mb-2">Zacznij wpisywać nazwę lub numer obrębu (zera wiodące są ignorowane), potem numer działki.</p>
            <div className="space-y-2">
              {/* Combobox obrębu — input tekstowy + lista sugestii pod spodem.
                  Pokazuje się po pierwszym znaku. Normalizacja zer wiodących
                  — "1" matchuje "001", "01", "1"; obsługuje do 3 cyfr numeru. */}
              <div className="relative">
                <input
                  ref={parcelSearchObrebRef}
                  type="text"
                  value={parcelSearchObrebInput}
                  onChange={(e) => {
                    setParcelSearchObrebInput(e.target.value);
                    // User zmienia input → reset wybranego obrębu
                    setParcelSearchObreb("");
                    setParcelSearchObrebOpen(true);
                  }}
                  onFocus={() => {
                    if (parcelSearchObrebInput.trim().length > 0) setParcelSearchObrebOpen(true);
                  }}
                  onBlur={() => {
                    // Delay żeby klik w sugestię zdążył się zarejestrować (onMouseDown)
                    setTimeout(() => setParcelSearchObrebOpen(false), 120);
                  }}
                  placeholder="Obręb — np. '1', 'Dąbrówka', '0001'"
                  className="w-full pl-3 pr-28 py-2.5 border rounded bg-white text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {parcelSearchObreb && (
                    <span
                      className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded pointer-events-none"
                      title={`Wybrano: ${parcelSearchObreb}`}>
                      ✓ wybrano
                    </span>
                  )}
                  {(parcelSearchObrebInput || parcelSearchObreb) && (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setParcelSearchObreb("");
                        setParcelSearchObrebInput("");
                        setParcelSearchObrebOpen(false);
                        parcelSearchObrebRef.current?.focus();
                      }}
                      className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                      title="Wyczyść obręb"
                      aria-label="Wyczyść obręb">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {parcelSearchObrebOpen && obrebSuggestions.length > 0 && (
                  <ul className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border rounded shadow-lg max-h-64 overflow-auto">
                    {obrebSuggestions.map((p) => (
                      <li
                        key={p.value}
                        onMouseDown={(e) => {
                          // onMouseDown żeby zdążyć przed onBlur inputa
                          e.preventDefault();
                          setParcelSearchObreb(p.value);
                          setParcelSearchObrebInput(p.label);
                          setParcelSearchObrebOpen(false);
                        }}
                        className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 border-b last:border-b-0">
                        <span className="font-medium">{p.label}</span>
                        <span className="text-gray-500 text-xs ml-2">({p.value})</span>
                      </li>
                    ))}
                  </ul>
                )}
                {parcelSearchObrebOpen &&
                  parcelSearchObrebInput.trim().length > 0 &&
                  obrebSuggestions.length === 0 && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border rounded shadow-lg px-3 py-2 text-sm text-gray-500">
                      Brak obrębów pasujących do „{parcelSearchObrebInput}"
                    </div>
                  )}
              </div>
              <div className="flex gap-1">
                <input
                  ref={parcelSearchNumerRef}
                  type="text"
                  placeholder="Nr działki (np. 289 lub 289/1)"
                  value={parcelSearchNumer}
                  onChange={(e) => setParcelSearchNumer(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleParcelSearch()}
                  className="flex-1 min-w-0 px-3 py-2.5 border rounded bg-white text-base focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <Button size="default" onClick={handleParcelSearch} disabled={parcelSearchLoading || !parcelSearchNumer.trim() || !parcelSearchObreb}>
                  <Search className="h-5 w-5 mr-1" /> Szukaj
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mt-3">
              <MapPin className="h-4 w-4 shrink-0" style={theme.accentText} />
              <span>Możesz też kliknąć bezpośrednio na mapie, aby zaznaczyć działkę</span>
            </div>
          </div>

          {/* Konsultant: wybrane (kliknięte / wyszukane) działki z udziałem
              przeznaczeń terenu — bez składania wniosku, z notatką projektanta. */}
          {consultantMode && selectedParcels.length > 0 && (
          <div className="border-b pb-4">
            <h2 className="text-lg font-bold mb-3" style={theme.sectionTitle}>
              Wybrane działki ({selectedParcels.length})
            </h2>
            <div className="space-y-2">
              {selectedParcels.map((p) => (
                <div key={p.id} className="bg-green-50 border border-green-300 rounded px-4 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-base font-medium text-green-800 break-all">{p.id}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => onConsultantParcelClick?.(p.id)}
                        className="text-gray-500 hover:text-gray-800"
                        title="Notatka projektanta">
                        <StickyNote className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setSelectedParcels((prev) => prev.filter((x) => x.id !== p.id))}
                        className="text-red-400 hover:text-red-600"
                        title="Usuń z listy">
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  {activeProcedure && mpzpZonesStatus.data?.hasZones && (
                    <ParcelZonesInfo
                      tenant={tenant}
                      procedureId={activeProcedure.id}
                      parcelId={p.id}
                      geometry={p.geometry}
                      hasPlanText={!!(activeProcedure.payload as any)?.mpzpPlanText}
                      files={activeProcedure.files}
                      sourceLabel={(activeProcedure.payload as any)?.mpzpZonesLabel}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Złóż wniosek/uwagę LUB Wybrane działki — pominięte w trybie
              konsultanta (klik w działkę otwiera modal notatki, brak zbierania
              listy działek do submit-u). */}
          {!consultantMode && (
          <div className="border-b pb-4">
            {selectedParcels.length > 0 ? (
              <>
                <h2 className="flex items-center gap-2 text-lg font-bold mb-3" style={theme.sectionTitle}>
                  <StepBadge n={2} style={theme.header} />
                  Wybrane działki ({selectedParcels.length})
                </h2>
                <div className="space-y-2 mb-3">
                  {selectedParcels.map((p) => (
                    <div key={p.id} className="bg-green-50 border border-green-300 rounded px-4 py-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-base font-medium text-green-800">{p.id}</span>
                        <button onClick={() => setSelectedParcels((prev) => prev.filter((x) => x.id !== p.id))} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                      {activeProcedure && mpzpZonesStatus.data?.hasZones && (
                        <ParcelZonesInfo
                          tenant={tenant}
                          procedureId={activeProcedure.id}
                          parcelId={p.id}
                          geometry={p.geometry}
                          hasPlanText={!!(activeProcedure.payload as any)?.mpzpPlanText}
                          files={activeProcedure.files}
                          sourceLabel={(activeProcedure.payload as any)?.mpzpZonesLabel}
                        />
                      )}
                    </div>
                  ))}
                </div>
                {activeProcedure && (
                  <button
                    onClick={goToApplication}
                    className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-base font-medium py-3 px-4 rounded-md transition-colors">
                    <MapPin className="h-5 w-5" />
                    Złóż {pageSettings?.applicationType === "uwaga" ? "uwagę" : "wniosek"} dla {selectedParcels.length} działek
                  </button>
                )}
                <p className="text-sm text-gray-400 mt-2">Kliknij na mapie aby dodać kolejne działki lub usuń niepotrzebne.</p>
              </>
            ) : (
              <>
                <h2 className="flex items-center gap-2 text-lg font-bold mb-3" style={theme.sectionTitle}>
                  <StepBadge n={2} style={theme.header} />
                  {pageSettings?.applicationType === "uwaga" ? "Złóż uwagę do planu" : "Złóż wniosek do planu"}
                </h2>
                {displayedProcedures.length === 0 ? (
                  <p className="text-base text-gray-500">Brak aktywnych procedur.</p>
                ) : (
                  <div className="space-y-3">
                    {displayedProcedures.map((proc) => {
                      const from = new Date(proc.activeFrom);
                      const to = new Date(proc.activeTo);
                      const isActive = isProcedureActive(proc);
                      const fromStr = from.toLocaleDateString("pl-PL");
                      const toStr = to.toLocaleDateString("pl-PL");
                      // Na landing page (isSingleProcedureMode=false) cała karta
                      // prowadzi do dedykowanej strony procedury /{tenant}/{slug}.
                      // W single-procedure mode karta jest statyczna (już jesteś na
                      // tej stronie) — akcja "Złóż wniosek" prowadzi do formularza.
                      if (!isSingleProcedureMode) {
                        return (
                          <Link
                            key={proc.id}
                            to="/$tenant/$procedureSlug"
                            params={{ tenant, procedureSlug: proc.name }}
                            className="block border border-gray-200 rounded-lg p-5 bg-white hover:border-gray-400 hover:shadow-sm transition-all group">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <h3 className="font-semibold text-base group-hover:underline" style={theme.sectionTitle}>
                                {proc.displayName}
                              </h3>
                              {isActive ? (
                                <span className="text-xs font-medium px-2 py-0.5 rounded bg-green-100 text-green-800 shrink-0">
                                  Aktywna
                                </span>
                              ) : (
                                <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">
                                  Nieaktywna
                                </span>
                              )}
                            </div>
                            {proc.description && (
                              <p className="text-sm text-gray-600 mb-2 line-clamp-3">{proc.description}</p>
                            )}
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>
                                {fromStr} — {toStr}
                              </span>
                            </div>
                            <div className="mt-3 text-sm font-medium flex items-center gap-1" style={theme.accentText}>
                              <span>Przejdź do systemu składania uwag lub wniosków</span>
                              <ChevronRight className="h-4 w-4" />
                            </div>
                          </Link>
                        );
                      }
                      // single-procedure view
                      return (
                        <div key={proc.id} className="border border-gray-200 rounded-lg p-5 bg-white">
                          <h3 className="font-semibold text-base mb-2" style={theme.sectionTitle}>
                            {proc.displayName}
                          </h3>
                          {proc.description && (
                            <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">{proc.description}</p>
                          )}
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>
                              {fromStr} — {toStr}
                            </span>
                          </div>
                          {isActive && (
                            <Link
                              to="/$tenant/$formId/application"
                              params={{ tenant, formId: proc.id }}
                              className="mt-4 block text-center bg-green-600 text-white px-4 py-2.5 rounded text-base font-medium hover:bg-green-700">
                              {pageSettings?.applicationType === "uwaga" ? "Złóż uwagę" : "Złóż wniosek"}
                            </Link>
                          )}
                          {isActive && (proc.payload as any)?.survey?.enabled && (
                            <Link
                              to="/$tenant/$formId/geoankieta"
                              params={{ tenant, formId: proc.id }}
                              className="mt-2 block text-center border border-violet-300 bg-violet-50 text-violet-800 px-4 py-2 rounded text-sm font-medium hover:bg-violet-100">
                              Wypełnij geoankietę
                            </Link>
                          )}
                          {!isActive && isAdminHere && from > new Date() && (
                            <AdminTestPanel
                              tenant={tenant}
                              formId={proc.id}
                              from={from}
                              letter={pageSettings?.applicationType === "uwaga" ? "uwagi" : "wniosku"}
                              surveyEnabled={!!(proc.payload as any)?.survey?.enabled}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-sm text-gray-400 mt-3">
                  Nie musisz się rejestrować. Możesz też kliknąć na mapie aby wybrać działki.
                </p>
              </>
            )}
          </div>
          )}

          {/* Logowanie — pomijamy w consultantMode (konsultant już zalogowany). */}
          {!consultantMode && (
          <div className="text-center pt-2">
            <Link to="/$tenant/login" params={{ tenant }} className="text-sm text-gray-400 hover:text-blue-600">
              Logowanie admin / konsultant
            </Link>
          </div>
          )}

      </>
    );
  }

  function renderMapContent() {
    return (
          <div className="h-full flex flex-col">
            {/* TOP ACTION BAR — duży widoczny guzik "Złóż wniosek/uwagę"
                (single-procedure) lub "Przejdź do systemu..." (lista).
                Ma być wyraźnie widoczny NAD mapą, żeby użytkownik nie musiał
                scrollować w dół lewej kolumny. */}
            {(() => {
              // W trybie konsultanta brak przycisków składania wniosków —
              // konsultant nie składa wniosków, dodaje notatki przez modal
              // otwierany kliknięciem działki na mapie.
              if (consultantMode) return null;
              const actionLabel =
                pageSettings?.applicationType === "uwaga" ? "Złóż uwagę" : "Złóż wniosek";
              // Tryb single-procedure: bezpośredni link do formularza aplikacji.
              if (isSingleProcedureMode && slugProcedure) {
                return (
                  <div
                    className="shrink-0 px-3 py-2 border-b border-gray-200 bg-white"
                    style={{ display: "flex", justifyContent: "flex-end" }}>
                    {(slugProcedure.payload as any)?.survey?.enabled && (
                      <Link
                        to="/$tenant/$formId/geoankieta"
                        params={{ tenant, formId: slugProcedure.id }}
                        className="inline-flex items-center justify-center gap-2 border border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 text-base font-medium px-5 py-3 rounded-md transition-colors mr-2">
                        <ClipboardList className="h-5 w-5" />
                        Wypełnij geoankietę
                      </Link>
                    )}
                    <button
                      onClick={goToApplication}
                      className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white text-base font-medium px-6 py-3 rounded-md transition-colors">
                      <MapPin className="h-5 w-5" />
                      {actionLabel} dla wybranych działek
                      {selectedParcels.length > 0 ? ` (${selectedParcels.length})` : ""}
                    </button>
                  </div>
                );
              }
              // Tryb listy: guzik prowadzi do strony procedury aktualnie
              // wybranej w prawym panelu (previewProcedureId).
              if (previewProcedure) {
                return (
                  <div
                    className="shrink-0 px-3 py-2 border-b border-gray-200 bg-white"
                    style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                    {(previewProcedure.payload as any)?.survey?.enabled && (
                      <Link
                        to="/$tenant/$formId/geoankieta"
                        params={{ tenant, formId: previewProcedure.id }}
                        className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-base font-medium px-6 py-3 rounded-md transition-colors">
                        <ClipboardList className="h-5 w-5" />
                        <span>Wypełnij geoankietę</span>
                      </Link>
                    )}
                    <Link
                      to="/$tenant/$procedureSlug"
                      params={{ tenant, procedureSlug: previewProcedure.name }}
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-base font-medium px-6 py-3 rounded-md transition-colors">
                      <MapPin className="h-5 w-5" />
                      <span>{pageSettings?.applicationType === "uwaga" ? "Złóż uwagę do projektu planu" : "Złóż wniosek do planu"}</span>
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  </div>
                );
              }
              return null;
            })()}
            {/* Map */}
            <div className="flex-1 relative">
              {/* Panel z narzędziami pomiaru + wyszukiwarką stref + legendą.
                  Przesunięty o ~38px w prawo (x=50) żeby nie zasłaniał
                  przycisków zoom +/- w lewym górnym rogu mapy. Startuje
                  otwarty; user może zwinąć do samego paska chevronem. */}
              <FloatingPanel
                title="Wyszukiwarka stref i legenda"
                defaultPosition={{ x: 50, y: 12 }}
                defaultMinimized={false}
                width={360}
                maxHeight={560}
                panelRegistry={panelRegistry}
                panelId="search-legend"
                contentOverflow="visible"
              >
                {/* Pomiary NAD wyszukiwarką i legendą */}
                <div
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "#fff",
                  }}>
                  <MeasureTools map={olMapInstance} embedded />
                </div>
                {/* MPZP — przyciski Legenda + Tekst planu (analogicznie do
                    układu jak ☰ Legenda w POG, ale dla planu miejscowego).
                    Widoczne gdy admin wgrał odpowiedni plik (sprawdzamy
                    obecność meta w payload — niezależnie od dokładnego
                    spatialPlanningActType, bo regex 'miejscowy/3.2' bywał
                    zawodny przy nietypowych nazwach typów). MpzpFilesButtons
                    sam zwróci null jeśli oba meta są undefined. */}
                {slugProcedure && (
                  <MpzpFilesButtons
                    tenantId={tenant}
                    formId={slugProcedure.id}
                    legendaMeta={(slugProcedure.payload as any)?.mpzpLegenda}
                    planTextMeta={(slugProcedure.payload as any)?.mpzpPlanText}
                    onOpenLegenda={() => setLegendaModalOpen(true)}
                    hasDocuments={(slugProcedure.files?.length ?? 0) > 0}
                    onOpenDocuments={() => setDocumentsModalOpen(true)}
                  />
                )}
                <PlanLayerSearchOverlay
                  tenant={tenant}
                  isSingleProcedureMode={isSingleProcedureMode}
                  slugProcedure={slugProcedure}
                  previewProcedureId={previewProcedureId}
                  procedures={procedures}
                  showLegend={showLegend}
                  onToggleLegend={() => setShowLegend((s) => !s)}
                  onSelectFromSearch={(f, query) => {
                    logSearch({ kind: "plan_zone", query, result: f.properties.oznaczenie ?? String(f.id), resultCount: 1 });
                    setPlanFocusFeatureId(f.id);
                    setSelectedPlanFeatures([f.properties]);
                  }}
                  onSearchMiss={(query) => logSearch({ kind: "plan_zone", query, resultCount: 0 })}
                  embedded
                />
              </FloatingPanel>
              <OlMap
                latLng={latLng}
                setLatLng={setLatLng}
                onParcelClick={handleParcelClick}
                selectedParcelsGeometries={selectedParcels}
                planContext={planContext}
                onPlanFeatureClick={setSelectedPlanFeatures}
                planFocusFeatureId={planFocusFeatureId}
                layerVisibility={layerVisibility}
                layerOpacity={layerOpacity}
                layerOrder={layerOrder}
                customWmsLayers={customWmsLayers}
                // Label akcji "wybór działki" zależny od typu procedury
                // (uwaga / wniosek). Obecność tego propsa włącza popup
                // wyboru akcji przy kliknięciu (zamiast autoakcji).
                parcelActionLabel={
                  consultantMode
                    ? "Dodaj notatkę projektanta"
                    : pageSettings?.applicationType === "uwaga"
                      ? "Wskaż działkę do uwagi"
                      : "Wskaż działkę do wniosku"
                }
                secondaryParcelAction={
                  consultantMode
                    ? {
                        label: "Nachylenie terenu działki",
                        onParcel: (info) => {
                          if (info.id) setSlopeRequest({ parcelId: info.id, nonce: Date.now() });
                        },
                      }
                    : undefined
                }
                immediateClick={consultantMode}
                allowClickOnSelectedParcel={consultantMode}
                // Mapa zawsze w PL-1992 (EPSG:2180) — natywny matrixSet WMTS
                // GUGIK, brak reprojekcji, ostre kafle. Pole `mapCrs` w panelu
                // gminy pozostaje jako opcja na przyszlosc (gdyby ktos chcial
                // strefy PL-2000 — wtedy odkomentowac warunek), ale w obecnej
                // konfiguracji ignorujemy ten wybor: PL-2000 wymagaloby
                // reprojekcji EPSG:3857 → strefa, a urzednik i tak operuje
                // na 2180 (ULDK, plany).
                rasterCogUrls={rasterCogUrls}
                mapCrsChoice="PL1992"
                initialZoom={initialZoom}
                // MeasureTools renderowany przez ten komponent wewnątrz
                // FloatingPanel "Wyszukiwarka stref i legenda" (żeby pomiary
                // były obok search/legend, nie oddzielnie w prawym górnym).
                hideMeasureTools
                onMapReady={setOlMapInstance}
              />
              {/* Warstwy mapy — pływający panel. Width 340 żeby długie
                  nazwy custom WMS (np. "obszar szczególnego zagrożenia
                  powodzią") mieściły się razem z checkboxem, uchwytem
                  drag i przyciskami 🎚/✕. */}
              <FloatingPanel
                title="Warstwy mapy"
                defaultPosition={{ x: 12, y: 400 }}
                defaultMinimized={false}
                width={340}
                maxHeight={450}
                panelRegistry={panelRegistry}
                panelId="layers"
              >
                <LayerControl
                  visibility={layerVisibility}
                  onChange={setLayerVisibility}
                  opacity={layerOpacity}
                  onOpacityChange={setLayerOpacity}
                  customLayers={customWmsLayers}
                  onCustomLayersChange={setCustomWmsLayers}
                  onOpenAddWms={() => setWmsDialogOpen(true)}
                  layerOrder={layerOrder}
                  onLayerOrderChange={setLayerOrder}
                  // Panel u użytkownika pokazuje dokładnie te same warstwy co
                  // admin w "Domyślne warstwy mapy" — kontekstowa lista dla
                  // typu procedury (POG/MPZP) minus `hiddenBuiltinLayerIds`
                  // z payload. Ten sam helper używany w EditProcedurePage,
                  // więc admin i public są ZAWSZE zgodne.
                  visibleLayerIds={getAvailableBuiltinLayerIds({
                    spatialPlanningActType: (slugProcedure?.payload as any)?.spatialPlanningActType,
                    hasRasterReady:
                      activeRasterMetaQ.data?.s3Key && activeRasterMetaQ.data?.status === "ready" || false,
                    hiddenBuiltinLayerIds: (slugProcedure?.payload as any)?.hiddenBuiltinLayerIds,
                  })}
                  layerLabels={{
                    raster: activeRasterMetaQ.data?.layerName || "Projekt MPZP",
                  }}
                />
              </FloatingPanel>
              {consultantMode && tenant && (
                <FloatingPanel
                  title="Nachylenie terenu"
                  defaultPosition={{ x: 420, y: 12 }}
                  defaultMinimized
                  width={360}
                  maxHeight={620}
                  panelRegistry={panelRegistry}
                  panelId="slope"
                  expandSignal={slopeRequest?.nonce}
                >
                  <Suspense fallback={<p className="p-3 text-xs text-gray-500">Ładowanie…</p>}>
                    <SlopePanel
                      map={olMapInstance}
                      tenantId={tenant}
                      parcelId={slopeRequest?.parcelId ?? null}
                      onClearParcel={() => setSlopeRequest(null)}
                    />
                  </Suspense>
                </FloatingPanel>
              )}
              {wmsDialogOpen && (
                <Suspense fallback={null}>
                  <AddWmsDialog
                    open={wmsDialogOpen}
                    onClose={() => setWmsDialogOpen(false)}
                    alreadyAddedCatalogIds={customWmsLayers.map((l) => l.catalogId).filter((x): x is string => !!x)}
                    onAdd={(newLayer) => {
                      const id = stableCustomLayerId({
                        catalogId: newLayer.catalogId,
                        url: newLayer.url,
                        layerName: newLayer.layerName,
                      });
                      setCustomWmsLayers((prev) => {
                        if (prev.some((l) => l.id === id)) return prev;
                        return [...prev, { ...newLayer, id, opacity: 0.8, visible: true }];
                      });
                    }}
                  />
                </Suspense>
              )}
              {/* Szczegóły planu — pływający panel. Key z fallbackami
                  (gmlId → localId → oznaczenie → symbol + count) żeby różne
                  kliknięcia zawsze remountowały komponent i resetowały pozycję
                  na defaultPosition (user widzi panel wracający na swoje
                  miejsce przy każdym nowym wyborze). zIndex=1200 — nad
                  search-legend (1000), żeby panel był zawsze widoczny nad
                  innymi pływającymi panelami. */}
              {selectedPlanFeatures.length > 0 && (() => {
                const primary = selectedPlanFeatures[0];
                const stableKey =
                  primary?.gmlId ||
                  primary?.localId ||
                  primary?.oznaczenie ||
                  `${primary?.symbol ?? "unknown"}-${selectedPlanFeatures.length}`;
                return (
                  <FloatingPanel
                    key={`details-${stableKey}`}
                    title="Szczegóły strefy"
                    defaultPosition={{ x: 50, y: 12 }}
                    width={360}
                    maxHeight={500}
                    zIndex={1200}
                    panelRegistry={panelRegistry}
                    panelId="details"
                    onClose={() => setSelectedPlanFeatures([])}
                  >
                    <PlanFeatureDetails features={selectedPlanFeatures} />
                  </FloatingPanel>
                );
              })()}
              {/* Modal podglądu legendy MPZP — overlay nad mapą.
                  Otwierany przyciskiem "📖 Legenda" w panelu search-legend. */}
              {slugProcedure && documentsModalOpen && (
                <div
                  className="fixed inset-0 z-[3000] bg-black/40 flex items-center justify-center p-4"
                  onClick={() => setDocumentsModalOpen(false)}>
                  <div
                    className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end px-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setDocumentsModalOpen(false)}
                        className="px-2 py-1 text-sm text-gray-600 hover:text-gray-900">
                        Zamknij ✕
                      </button>
                    </div>
                    <div className="px-2 pb-2">
                      <ProcedureDownloads
                        tenant={tenant}
                        procedureId={slugProcedure.id}
                        files={slugProcedure.files}
                        payload={slugProcedure.payload}
                      />
                    </div>
                  </div>
                </div>
              )}
              {slugProcedure && legendaModalOpen && (slugProcedure.payload as any)?.mpzpLegenda && (
                <MpzpLegendaModal
                  url={getMpzpFileUrl(tenant, slugProcedure.id, "legenda")}
                  filename={(slugProcedure.payload as any).mpzpLegenda.filename}
                  mimetype={(slugProcedure.payload as any).mpzpLegenda.mimetype}
                  onClose={() => setLegendaModalOpen(false)}
                />
              )}
              <style>{`
                @media (max-width: 640px) {
                  /* Na mobile panele mniejsze */
                  .plan-details-popup {
                    max-height: 50vh;
                  }
                }
              `}</style>
            </div>
            {/* Status bar */}
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-500 flex justify-between shrink-0">
              <span>Kliknij na mapie aby zaznaczyć działkę</span>
              <span>{selectedParcels.length > 0 ? `Wybrano: ${selectedParcels.length} działek` : "Brak wybranych działek"}</span>
            </div>
          </div>
    );
  }
}

/**
 * Wyszukiwarka stref + przycisk legendy + (rozwijana) legenda — overlay
 * w lewym górnym rogu mapy. Widoczny tylko gdy aktywna procedura jest
 * typu 'plan ogólny' i ma wgrany plan.
 *
 * Wydzielony komponent, bo używa useGetPlanFeaturesQuery / useGetPlanMetaQuery
 * które wymagają conditional enable.
 */
function PlanLayerSearchOverlay({
  tenant,
  isSingleProcedureMode,
  slugProcedure,
  previewProcedureId,
  procedures,
  showLegend,
  onToggleLegend,
  onSelectFromSearch,
  onSearchMiss,
  embedded,
}: {
  tenant: string;
  isSingleProcedureMode: boolean;
  slugProcedure: any;
  previewProcedureId: string | null;
  procedures: any[];
  showLegend: boolean;
  onToggleLegend: () => void;
  onSelectFromSearch: (feature: import("@/src/api/Plan").PlanFeatureCollection["features"][number], query: string) => void;
  onSearchMiss?: (query: string) => void;
  embedded?: boolean;
}) {
  const activeProc = isSingleProcedureMode
    ? slugProcedure
    : (procedures.find((p) => p.id === previewProcedureId) ?? null);
  const actType = activeProc?.payload?.spatialPlanningActType ?? "";
  const isPlanOgolny = actType.includes("3.1") || /plan og[oó]lny/i.test(actType);
  const procedureId = isPlanOgolny && activeProc ? activeProc.id : null;

  const featuresQ = useGetPlanFeaturesQuery(
    tenant,
    procedureId ?? "",
    {},
    { enabled: !!procedureId },
  );
  const metaQ = useGetPlanMetaQuery(tenant, procedureId ?? "", { enabled: !!procedureId });
  // Lokalny "filtr" widoczności dla legendy — tu nie przekazujemy go do mapy
  // (mapa pokazuje wszystkie features), ale checkbox'y w legendzie pełnią
  // rolę toggle widoczności w jej opisie. Pełne filtrowanie symboli wymagałoby
  // przekazania do OlMap kolejnego propsa — pominę to na ten moment, bo
  // user wymagał tylko widoczności LEGENDY z jej zawartością + całkowitego
  // wyłączenia obszarów (już obsłużone osobno przez LayerControl.areas).
  const [visibleSymbols, setVisibleSymbols] = useState<Set<string>>(new Set());

  if (!procedureId) return null;

  // Tryb embedded — cały content wewnątrz FloatingPanel (biały card z paddingiem,
  // search + przycisk legendy jako flex-row, legenda poniżej ze scrollem).
  if (embedded) {
    return (
      <div style={{ background: "#fff", padding: 10 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <PlanSearchBox data={featuresQ.data} onSelect={onSelectFromSearch} onSearchMiss={onSearchMiss} />
          </div>
          <button
            type="button"
            onClick={onToggleLegend}
            title={showLegend ? "Ukryj legendę" : "Pokaż legendę stref"}
            style={{
              padding: "8px 12px",
              fontSize: 13,
              background: showLegend ? "#2563eb" : "#fff",
              color: showLegend ? "#fff" : "#1f2937",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              cursor: "pointer",
              fontFamily: "system-ui, -apple-system, sans-serif",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}>
            ☰ Legenda
          </button>
        </div>
        {showLegend && (
          <div
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px solid #e5e7eb",
              maxHeight: 320,
              overflow: "auto",
              background: "#fff",
            }}>
            <PlanLegend
              meta={metaQ.data}
              visibleSymbols={visibleSymbols}
              setVisibleSymbols={setVisibleSymbols}
            />
          </div>
        )}
      </div>
    );
  }

  // Tryb floating (absolutny) — używany tam, gdzie nie ma FloatingPanelu
  // dookoła (legacy layout).
  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          width: "min(340px, calc(100vw - 24px))",
          zIndex: 1000,
          display: "flex",
          gap: 6,
        }}>
        <div style={{ flex: 1 }}>
          <PlanSearchBox data={featuresQ.data} onSelect={onSelectFromSearch} onSearchMiss={onSearchMiss} />
        </div>
        <button
          type="button"
          onClick={onToggleLegend}
          title={showLegend ? "Ukryj legendę" : "Pokaż legendę stref"}
          style={{
            padding: "8px 12px",
            fontSize: 13,
            background: showLegend ? "#2563eb" : "#fff",
            color: showLegend ? "#fff" : "#1f2937",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            cursor: "pointer",
            fontFamily: "system-ui, -apple-system, sans-serif",
            whiteSpace: "nowrap",
          }}>
          ☰ Legenda
        </button>
      </div>

      {showLegend && (
        <div
          style={{
            position: "absolute",
            top: 60,
            left: 12,
            width: "min(360px, calc(100vw - 24px))",
            maxHeight: "calc(100% - 80px)",
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: 12,
            overflow: "auto",
            zIndex: 1000,
          }}>
          <PlanLegend
            meta={metaQ.data}
            visibleSymbols={visibleSymbols}
            setVisibleSymbols={setVisibleSymbols}
          />
        </div>
      )}
    </>
  );
}

/** Okrągły numer kroku (1, 2…) w kolorze gminy — prowadzi mieszkańca
 *  przez stronę procedury: najpierw znajdź działkę, potem złóż pismo. */
function StepBadge({ n, style }: { n: number; style: React.CSSProperties }) {
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold"
      style={style}
      aria-hidden="true">
      {n}
    </span>
  );
}

/**
 * Przyciski "📖 Legenda" + "📄 Tekst planu" w panelu MPZP.
 * Każdy przycisk widoczny tylko jeśli admin wgrał odpowiedni plik.
 *
 * - Legenda → modal overlay (skalowany obraz/PDF z możliwością otwarcia w nowej karcie)
 * - Tekst planu → otwarcie w nowej karcie (PDF natywny viewer przeglądarki)
 */
function MpzpFilesButtons({
  tenantId,
  formId,
  legendaMeta,
  planTextMeta,
  onOpenLegenda,
  hasDocuments,
  onOpenDocuments,
}: {
  tenantId: string;
  formId: string;
  legendaMeta?: { filename: string; mimetype: string; fileSize: number };
  planTextMeta?: { filename: string; mimetype: string; fileSize: number };
  onOpenLegenda: () => void;
  hasDocuments?: boolean;
  onOpenDocuments?: () => void;
}) {
  if (!legendaMeta && !planTextMeta && !hasDocuments) return null;
  const planTextUrl = planTextMeta ? getMpzpFileUrl(tenantId, formId, "plan-text") : null;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        padding: "8px 10px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fff",
        flexWrap: "wrap",
      }}>
      {legendaMeta && (
        <button
          type="button"
          onClick={onOpenLegenda}
          title={`Otwórz legendę: ${legendaMeta.filename}`}
          style={mpzpButtonStyle}>
          📖 Legenda
        </button>
      )}
      {planTextMeta && planTextUrl && (
        <a
          href={planTextUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={`Tekst planu (${(planTextMeta.fileSize / 1024 / 1024).toFixed(1)} MB) — otworzy się w nowej karcie`}
          style={{ ...mpzpButtonStyle, textDecoration: "none" }}>
          📄 Tekst planu
        </a>
      )}
      {(hasDocuments || legendaMeta || planTextMeta) && onOpenDocuments && (
        <button
          type="button"
          onClick={onOpenDocuments}
          title="Wszystkie dokumenty projektu planu — pojedynczo lub w komplecie (ZIP)"
          style={mpzpButtonStyle}>
          ⬇ Dokumenty
        </button>
      )}
    </div>
  );
}

const mpzpButtonStyle: React.CSSProperties = {
  padding: "6px 12px",
  fontSize: 13,
  fontWeight: 500,
  background: "#fff",
  color: "#1f2937",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

/**
 * Modal overlay z podglądem legendy MPZP.
 *
 * - Obrazek w podglądzie z powiększaniem i przesuwaniem (ZoomPanImage:
 *   kółko myszy, przeciąganie, pinch, +/−/Dopasuj), start dopasowany do okna
 * - PDF w iframe (zoom i przewijanie daje przeglądarka)
 * - Klik w tło lub Esc zamyka
 * - Przycisk "Otwórz w nowej karcie" → pełny widok pliku
 */
function MpzpLegendaModal({
  url,
  filename,
  mimetype,
  onClose,
}: {
  url: string;
  filename: string;
  mimetype: string;
  onClose: () => void;
}) {
  // Esc zamyka
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const isPdf = mimetype === "application/pdf" || /\.pdf$/i.test(filename);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 16,
          maxWidth: "96vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 10,
            borderBottom: "1px solid #e5e7eb",
            marginBottom: 12,
            gap: 12,
          }}>
          <h3 style={{ margin: 0, fontSize: 16, fontFamily: "system-ui" }}>
            Legenda planu — {filename}
          </h3>
          <div style={{ display: "flex", gap: 6 }}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="Otwórz w nowej karcie (pełny widok)"
              style={{
                padding: "5px 10px",
                fontSize: 12,
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                textDecoration: "none",
              }}>
              ↗ Otwórz w nowej karcie
            </a>
            <button
              type="button"
              onClick={onClose}
              title="Zamknij (Esc)"
              style={{
                padding: "5px 10px",
                fontSize: 14,
                background: "transparent",
                border: "1px solid #d1d5db",
                borderRadius: 4,
                cursor: "pointer",
              }}>
              ✕
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: isPdf ? "auto" : "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {isPdf ? (
            <iframe
              src={url}
              title="Legenda planu"
              style={{
                width: "70vw",
                height: "70vh",
                border: "none",
              }}
            />
          ) : (
            <ZoomPanImage
              src={url}
              alt="Legenda planu"
              style={{ width: "min(calc(96vw - 32px), 1400px)", height: "76vh" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Panel testowy administratora — widoczny wyłącznie dla zalogowanego admina
 * tej gminy przy procedurze, która jeszcze nie wystartowała. Publiczne
 * przyciski są wtedy ukryte, a admin musi przejść oba formularze przed
 * dniem startu. Uwaga: formularz uwagi nie ma trybu testowego — testowe
 * pismo trafia na listę jak zwykłe (admin oznacza je statusem „odrzucona”
 * z notatką „test” albo prosi 4GIS o usunięcie). Geoankieta ma tryb
 * testowy (`?test=1` + token admina → odpowiedź oznaczona TEST, poza
 * statystykami i eksportami).
 */
function AdminTestPanel({
  tenant,
  formId,
  from,
  letter,
  surveyEnabled,
  compact,
}: {
  tenant: string;
  formId: string;
  from: Date;
  letter: string;
  surveyEnabled: boolean;
  compact?: boolean;
}) {
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className={`${compact ? "mb-3" : "mt-4"} rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900`}>
      <p className="text-sm font-semibold flex items-center gap-1.5">
        <FlaskConical className="h-4 w-4" /> Tryb testowy administratora
      </p>
      <p className="text-xs mt-1">
        Konsultacje startują {from.toLocaleDateString("pl-PL")} — do tego czasu mieszkańcy nie widzą przycisków. Ty możesz przetestować formularze
        już teraz (ten panel widzą tylko zalogowani administratorzy gminy).
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Link
          to="/$tenant/$formId/application"
          params={{ tenant, formId }}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700">
          <MapPin className="h-4 w-4" /> Testuj składanie {letter}
        </Link>
        {surveyEnabled && (
          <Link
            to="/$tenant/$formId/geoankieta"
            params={{ tenant, formId }}
            search={{ test: "1" } as never}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700">
            <ClipboardList className="h-4 w-4" /> Testuj geoankietę
          </Link>
        )}
      </div>
      <p className="text-[11px] mt-2 text-amber-800">
        Testowa odpowiedź geoankiety jest oznaczana jako TEST (poza statystykami i eksportem). Testowa {letter === "uwagi" ? "uwaga" : "wniosek"} trafia na listę
        pism jak zwykłe pismo — po teście ustaw status „odrzucona” z notatką „test”.
      </p>
    </div>
  );
}

export default TenantPublicPage;
